import proj4 from 'proj4';

import { getProj4Definition } from './projections';

/**
 * Module augmentation in order to extend the return type
 * of proj.Proj() which hides this value in its return data.
 */
declare module 'proj4' {
  interface InterfaceProjection {
    to_meter?: number;
  }
}

export type LinearUnit = 'm' | 'ft';

export type UnitSymbols = {
  length: string;
  area: string;
  volume: string;
};

const FOOT_IN_METERS = 0.3048;

const UNIT_SYMBOLS: Record<LinearUnit, UnitSymbols> = {
  m: { length: 'm', area: 'm²', volume: 'm³' },
  ft: { length: 'ft', area: 'ft²', volume: 'ft³' },
};

/**
 * Returns the linear unit of a coordinate system, given as an alias, a proj4 definition or WKT.
 *
 * International feet and US survey feet both count as feet, they only differ by 2 ppm.
 * Every other unit, and geographic coordinate systems, fall back to meters.
 */
export function getLinearUnit(aliasOrDefinition?: string): LinearUnit {
  if (!aliasOrDefinition) {
    return 'm';
  }

  let toMeter: number | undefined;
  try {
    toMeter = proj4.Proj(getProj4Definition(aliasOrDefinition)).to_meter;
  } catch {
    return 'm';
  }

  return toMeter !== undefined && Math.abs(toMeter - FOOT_IN_METERS) < 1e-5 ? 'ft' : 'm';
}

/**
 * Returns how many meters one unit of a coordinate system is, given as an alias, a proj4 definition
 * or WKT: 0.3048 for international feet, 0.3048006096 for US survey feet. Unlike getLinearUnit it
 * tells the two feet apart, for lengths that must match the backend's exactly.
 *
 * Geographic coordinate systems, missing and invalid definitions give 1.
 */
export function getMetersPerUnit(aliasOrDefinition?: string): number {
  if (!aliasOrDefinition) {
    return 1;
  }

  try {
    return proj4.Proj(getProj4Definition(aliasOrDefinition)).to_meter ?? 1;
  } catch {
    return 1;
  }
}

export function getUnitSymbols(unit: LinearUnit): UnitSymbols {
  return UNIT_SYMBOLS[unit];
}

/** The unit a GSD (ground sample distance) is given in: meters, or inches in feet projects. */
export type GsdUnit = 'm' | 'in';

const GSD_UNIT_IN_METERS: Record<GsdUnit, number> = { m: 1, in: 0.0254 };

export function getGsdUnit(linearUnit: LinearUnit): GsdUnit {
  return linearUnit === 'ft' ? 'in' : 'm';
}

/** Converts a GSD to meters, which the API takes. */
export function gsdToMeters(value: number, unit: GsdUnit): number {
  return value * GSD_UNIT_IN_METERS[unit];
}

/**
 * Converts a GSD from meters. Inches are rounded to 3 decimals; meters are kept as they are,
 * so saving an unchanged value keeps the stored one.
 */
export function gsdFromMeters(meters: number, unit: GsdUnit): string {
  if (unit === 'm') {
    return String(meters);
  }

  return String(Math.round((meters / GSD_UNIT_IN_METERS[unit]) * 1000) / 1000);
}
